/**
 * DocumentsPage - Research Hub with faceted filters and card grid
 * Replaces simple document list with 2-column layout (sidebar filters + main grid)
 */

import React, { useEffect, useState, useMemo, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Paper,
  TextField,
  CircularProgress,
  Alert,
  Chip,
  Card,
  CardContent,
  CardActionArea,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  InputAdornment,
  Button,
  Checkbox,
  FormControlLabel,
  FormGroup,
} from '@mui/material';
import {
  Search as SearchIcon,
  PictureAsPdf as PdfIcon,
  Headphones as AudioIcon,
  Videocam as VideoIcon,
  Article as DocIcon,
  Chat as ChatIcon,
} from '@mui/icons-material';
import { useLanguage } from '../contexts/LanguageContext';
import { API_ENDPOINTS, API_KEY } from '../config/api';

interface Document {
  id: string;
  title: string;
  source: string;
  report_date: string;
  analyst_names: string[];
  company_tickers: string[];
  created_at: string;
  doc_type?: string;
  report_type?: string;
  sector?: string;
  summary_html?: string;
}

type TimeRange = '1W' | '1M' | '3M' | '6M' | '1Y' | 'ALL';
type SortOption = 'date' | 'relevance';

const TIME_RANGES: { key: TimeRange; label: string; days: number }[] = [
  { key: '1W', label: '1W', days: 7 },
  { key: '1M', label: '1M', days: 30 },
  { key: '3M', label: '3M', days: 90 },
  { key: '6M', label: '6M', days: 180 },
  { key: '1Y', label: '1Y', days: 365 },
  { key: 'ALL', label: 'All', days: 0 },
];

const getDocIcon = (docType?: string) => {
  if (!docType) return <PdfIcon sx={{ color: '#e53935' }} />;
  const t = docType.toLowerCase();
  if (t.includes('audio') || t.includes('transcript')) return <AudioIcon sx={{ color: '#7b1fa2' }} />;
  if (t.includes('video')) return <VideoIcon sx={{ color: '#1565c0' }} />;
  return <PdfIcon sx={{ color: '#e53935' }} />;
};

const DocumentsPage: React.FC = () => {
  const { t } = useLanguage();
  const navigate = useNavigate();
  const [documents, setDocuments] = useState<Document[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [timeRange, setTimeRange] = useState<TimeRange>('3M');
  const [selectedSectors, setSelectedSectors] = useState<Set<string>>(new Set());
  const [selectedAnalysts, setSelectedAnalysts] = useState<Set<string>>(new Set());
  const [selectedTypes, setSelectedTypes] = useState<Set<string>>(new Set());
  const [sortBy, setSortBy] = useState<SortOption>('date');
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const handleSearchChange = useCallback((value: string) => {
    setSearchInput(value);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => setSearch(value), 300);
  }, []);

  useEffect(() => {
    return () => { if (debounceRef.current) clearTimeout(debounceRef.current); };
  }, []);

  // Fetch documents
  useEffect(() => {
    const fetchDocuments = async () => {
      try {
        setLoading(true);
        const response = await fetch(API_ENDPOINTS.documents, {
          headers: {
            'Content-Type': 'application/json',
            'X-API-Key': API_KEY,
          },
        });
        if (!response.ok) throw new Error('Failed to fetch documents');
        const data = await response.json();
        setDocuments(data.documents || []);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to load documents');
      } finally {
        setLoading(false);
      }
    };
    fetchDocuments();
  }, []);

  // Derive facet data
  const facets = useMemo(() => {
    const sectors = new Map<string, number>();
    const analysts = new Map<string, number>();
    const types = new Map<string, number>();

    for (const doc of documents) {
      const sector = doc.sector || 'Unknown';
      sectors.set(sector, (sectors.get(sector) || 0) + 1);

      if (doc.analyst_names) {
        for (const a of doc.analyst_names) {
          analysts.set(a, (analysts.get(a) || 0) + 1);
        }
      }

      const docType = doc.doc_type || doc.report_type || 'PDF';
      types.set(docType, (types.get(docType) || 0) + 1);
    }

    return {
      sectors: Array.from(sectors.entries()).sort((a, b) => b[1] - a[1]),
      analysts: Array.from(analysts.entries()).sort((a, b) => b[1] - a[1]).slice(0, 20),
      types: Array.from(types.entries()).sort((a, b) => b[1] - a[1]),
    };
  }, [documents]);

  // Filter documents
  const filteredDocs = useMemo(() => {
    let filtered = documents;

    // Time range
    if (timeRange !== 'ALL') {
      const rangeDef = TIME_RANGES.find(r => r.key === timeRange);
      if (rangeDef && rangeDef.days > 0) {
        const cutoff = new Date();
        cutoff.setDate(cutoff.getDate() - rangeDef.days);
        const cutoffStr = cutoff.toISOString().split('T')[0];
        filtered = filtered.filter(d => (d.report_date || d.created_at || '') >= cutoffStr);
      }
    }

    // Sector filter
    if (selectedSectors.size > 0) {
      filtered = filtered.filter(d => {
        const sector = d.sector || 'Unknown';
        return selectedSectors.has(sector);
      });
    }

    // Analyst filter
    if (selectedAnalysts.size > 0) {
      filtered = filtered.filter(d =>
        d.analyst_names?.some(a => selectedAnalysts.has(a))
      );
    }

    // Type filter
    if (selectedTypes.size > 0) {
      filtered = filtered.filter(d => {
        const docType = d.doc_type || d.report_type || 'PDF';
        return selectedTypes.has(docType);
      });
    }

    // Search
    if (search) {
      const q = search.toLowerCase();
      filtered = filtered.filter(d =>
        d.title?.toLowerCase().includes(q) ||
        d.company_tickers?.some(t => t.toLowerCase().includes(q)) ||
        d.analyst_names?.some(a => a.toLowerCase().includes(q))
      );
    }

    // Sort
    filtered = [...filtered].sort((a, b) => {
      if (sortBy === 'date') {
        return (b.report_date || b.created_at || '').localeCompare(a.report_date || a.created_at || '');
      }
      return 0;
    });

    return filtered;
  }, [documents, search, timeRange, selectedSectors, selectedAnalysts, selectedTypes, sortBy]);

  const handleDocumentClick = (doc: Document) => {
    const isMedia = ['Audio', 'audio', 'Video', 'video', 'transcript', 'media'].includes(doc.doc_type || '');
    if (isMedia) {
      window.open(`/replay-center?docId=${doc.id}`, '_blank');
    } else {
      window.open(`/document-pro?id=${doc.id}`, '_blank');
    }
  };

  const toggleSetItem = (set: Set<string>, item: string): Set<string> => {
    const next = new Set(set);
    if (next.has(item)) next.delete(item);
    else next.add(item);
    return next;
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '80vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">{error}</Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ display: 'flex', height: 'calc(100vh - 64px)', overflow: 'hidden' }}>
      {/* Sidebar Filters */}
      <Box sx={{
        width: 240,
        flexShrink: 0,
        borderRight: '1px solid #e0e0e0',
        overflow: 'auto',
        bgcolor: 'white',
        p: 2,
        display: { xs: 'none', md: 'block' },
      }}>
        <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1.5 }}>
          {t('research.title')}
        </Typography>

        {/* Search */}
        <TextField
          fullWidth
          size="small"
          placeholder={t('documents.searchPlaceholder')}
          value={searchInput}
          onChange={(e) => handleSearchChange(e.target.value)}
          InputProps={{
            startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment>,
          }}
          sx={{ mb: 2 }}
        />

        {/* Time Range */}
        <Typography variant="caption" sx={{ fontWeight: 600, color: '#666', mb: 0.5, display: 'block' }}>
          Time Range
        </Typography>
        <Box sx={{ display: 'flex', gap: 0.5, mb: 2, flexWrap: 'wrap' }}>
          {TIME_RANGES.map(r => (
            <Button
              key={r.key}
              size="small"
              variant={timeRange === r.key ? 'contained' : 'outlined'}
              onClick={() => setTimeRange(r.key)}
              sx={{ minWidth: 36, px: 1, py: 0.25, fontSize: '0.7rem' }}
            >
              {r.label}
            </Button>
          ))}
        </Box>

        {/* Content Type */}
        <Typography variant="caption" sx={{ fontWeight: 600, color: '#666', mb: 0.5, display: 'block' }}>
          Content Type
        </Typography>
        <FormGroup sx={{ mb: 2 }}>
          {facets.types.slice(0, 8).map(([type, count]) => (
            <FormControlLabel
              key={type}
              control={
                <Checkbox
                  size="small"
                  checked={selectedTypes.has(type)}
                  onChange={() => setSelectedTypes(prev => toggleSetItem(prev, type))}
                />
              }
              label={
                <Typography variant="caption">
                  {type} <span style={{ color: '#999' }}>({count})</span>
                </Typography>
              }
              sx={{ mx: 0, '& .MuiFormControlLabel-label': { fontSize: '0.8rem' } }}
            />
          ))}
        </FormGroup>

        {/* Sector */}
        <Typography variant="caption" sx={{ fontWeight: 600, color: '#666', mb: 0.5, display: 'block' }}>
          {t('table.sector')}
        </Typography>
        <FormGroup sx={{ mb: 2 }}>
          {facets.sectors.slice(0, 12).map(([sector, count]) => (
            <FormControlLabel
              key={sector}
              control={
                <Checkbox
                  size="small"
                  checked={selectedSectors.has(sector)}
                  onChange={() => setSelectedSectors(prev => toggleSetItem(prev, sector))}
                />
              }
              label={
                <Typography variant="caption">
                  {sector} <span style={{ color: '#999' }}>({count})</span>
                </Typography>
              }
              sx={{ mx: 0, '& .MuiFormControlLabel-label': { fontSize: '0.8rem' } }}
            />
          ))}
        </FormGroup>

        {/* Analyst */}
        <Typography variant="caption" sx={{ fontWeight: 600, color: '#666', mb: 0.5, display: 'block' }}>
          {t('table.analyst')}
        </Typography>
        <FormGroup sx={{ mb: 2 }}>
          {facets.analysts.slice(0, 10).map(([analyst, count]) => (
            <FormControlLabel
              key={analyst}
              control={
                <Checkbox
                  size="small"
                  checked={selectedAnalysts.has(analyst)}
                  onChange={() => setSelectedAnalysts(prev => toggleSetItem(prev, analyst))}
                />
              }
              label={
                <Typography variant="caption">
                  {analyst} <span style={{ color: '#999' }}>({count})</span>
                </Typography>
              }
              sx={{ mx: 0, '& .MuiFormControlLabel-label': { fontSize: '0.8rem' } }}
            />
          ))}
        </FormGroup>
      </Box>

      {/* Main Content */}
      <Box sx={{ flex: 1, overflow: 'auto', p: { xs: 2, md: 3 } }}>
        {/* Header */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="h5" sx={{ fontWeight: 700 }}>
            {t('research.title')}
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Typography variant="body2" color="text.secondary">
              {filteredDocs.length} / {documents.length} {t('documents.title').toLowerCase()}
            </Typography>
            <FormControl size="small" sx={{ minWidth: 130 }}>
              <InputLabel>{t('research.sortBy')}</InputLabel>
              <Select
                value={sortBy}
                label={t('research.sortBy')}
                onChange={e => setSortBy(e.target.value as SortOption)}
              >
                <MenuItem value="date">{t('research.dateNewest')}</MenuItem>
                <MenuItem value="relevance">{t('research.relevance')}</MenuItem>
              </Select>
            </FormControl>
          </Box>
        </Box>

        {/* Mobile search (shown only on mobile) */}
        <Box sx={{ display: { xs: 'block', md: 'none' }, mb: 2 }}>
          <TextField
            fullWidth
            size="small"
            placeholder={t('documents.searchPlaceholder')}
            value={searchInput}
            onChange={(e) => handleSearchChange(e.target.value)}
            InputProps={{
              startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment>,
            }}
          />
        </Box>

        {/* Document Grid */}
        <Box sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr', lg: '1fr 1fr 1fr' },
          gap: 2,
        }}>
          {filteredDocs.map(doc => (
            <Card key={doc.id} elevation={1} sx={{ '&:hover': { boxShadow: 4 }, transition: 'box-shadow 0.2s' }}>
              <CardActionArea onClick={() => handleDocumentClick(doc)}>
                <CardContent sx={{ p: 2 }}>
                  <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, mb: 1 }}>
                    {getDocIcon(doc.doc_type)}
                    <Typography variant="subtitle2" sx={{ fontWeight: 600, lineHeight: 1.3, flex: 1 }} noWrap title={doc.title}>
                      {doc.title || 'Untitled'}
                    </Typography>
                  </Box>

                  <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', mb: 1 }}>
                    {doc.company_tickers?.slice(0, 3).map(ticker => (
                      <Chip key={ticker} label={ticker} size="small" color="primary" sx={{ fontSize: '0.7rem', height: 22 }} />
                    ))}
                    {(doc.company_tickers?.length || 0) > 3 && (
                      <Chip label={`+${doc.company_tickers!.length - 3}`} size="small" variant="outlined" sx={{ fontSize: '0.7rem', height: 22 }} />
                    )}
                  </Box>

                  <Typography variant="caption" color="text.secondary" noWrap sx={{ display: 'block', mb: 0.5 }}>
                    {doc.analyst_names?.join(', ') || 'Unknown analyst'}
                  </Typography>

                  {doc.summary_html && (
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{
                        display: '-webkit-box',
                        WebkitLineClamp: 2,
                        WebkitBoxOrient: 'vertical',
                        overflow: 'hidden',
                        lineHeight: 1.3,
                        mb: 0.5,
                      }}
                    >
                      {doc.summary_html.replace(/<[^>]+>/g, '').substring(0, 150)}
                    </Typography>
                  )}

                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 1 }}>
                    <Chip
                      label={doc.report_type || doc.source || 'Report'}
                      size="small"
                      variant="outlined"
                      sx={{ fontSize: '0.65rem', height: 20 }}
                    />
                    <Typography variant="caption" color="text.secondary">
                      {doc.report_date || 'No date'}
                    </Typography>
                  </Box>
                </CardContent>
              </CardActionArea>
            </Card>
          ))}
        </Box>

        {/* Empty state */}
        {filteredDocs.length === 0 && (
          <Paper sx={{ p: 4, textAlign: 'center', mt: 2 }}>
            <DocIcon sx={{ fontSize: 48, color: '#ccc', mb: 1 }} />
            <Typography color="text.secondary" gutterBottom>
              No documents match your filters
            </Typography>
            <Button
              variant="outlined"
              startIcon={<ChatIcon />}
              onClick={() => navigate(`/agent${search ? `?q=${encodeURIComponent(search)}` : ''}`)}
              sx={{ mt: 1 }}
            >
              Ask Sarah instead
            </Button>
          </Paper>
        )}
      </Box>
    </Box>
  );
};

export default DocumentsPage;
